import React, { useContext, useEffect, useState } from 'react';
import { Route, Redirect } from 'react-router-dom';
import TokenService from '../../services/token-service';
import UserService from '../../services/UserService';
import UserContext from '../../contexts/UserContext';

// Guards admin-only routes. Renders the component only for users whose
// account has `is_admin`; everyone else is redirected home. Because
// `currentUser` is repopulated asynchronously on load (see TopBar), we
// resolve the admin flag from context when available and otherwise fetch
// the current user before deciding, so real admins aren't bounced during
// the load window.
export default function AdminRoute({ component, ...props }) {
  const Component = component;
  const userContext = useContext(UserContext);
  // 'checking' | 'allowed' | 'denied'
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    let cancelled = false;

    if (!TokenService.hasAuthToken()) {
      setStatus('denied');
      return;
    }

    // Use the already-loaded user if we know its admin flag.
    if (userContext.currentUser && userContext.currentUser.is_admin !== undefined) {
      setStatus(userContext.currentUser.is_admin ? 'allowed' : 'denied');
      return;
    }

    UserService.getUserByToken()
      .then((res) => {
        if (cancelled) return;
        // TEMP DIAGNOSTIC: confirm what the live API returns for the admin flag.
        console.log('[AdminRoute] getUserByToken user:', res?.user);
        setStatus(res?.user?.is_admin ? 'allowed' : 'denied');
      })
      .catch(() => {
        if (!cancelled) setStatus('denied');
      });

    return () => {
      cancelled = true;
    };
  }, [userContext.currentUser]);

  return (
    <Route
      {...props}
      render={(componentProps) => {
        if (status === 'checking') {
          return <p className="loading-message">Loading…</p>;
        }
        if (status === 'allowed') {
          return <Component {...componentProps} />;
        }
        // Denied: show an inline message rather than redirecting to '/',
        // which would re-trigger the Auth0 login flow (authPage).
        return (
          <div role="alert" className="admin-access-denied" style={{ padding: '2em' }}>
            <h2>Access denied</h2>
            <p>You don’t have permission to view the admin page.</p>
          </div>
        );
      }}
    />
  );
}
